import React, { useEffect, useState } from 'react';
import { Spinner, Alert, Button, Modal, Breadcrumb, Row, Col } from 'react-bootstrap';
import Base from '../Base';
import instance from '../../services/baseService';
import { ROUTES } from '../../constants/roles';
import toast from 'react-hot-toast';
import CreateRoleOffCanvas from './CreateRoleOffCanvas';
import { NavLink } from 'react-router-dom';
import DeleteRoles from './DeleteRoles'; // or the correct relative path
import { FaPlus } from 'react-icons/fa';
import { truncateText } from '../../utils';
import { extractErrorMessage } from '../../constants/common';

const ViewRoles = () => {
  const [tabList, setTabList] = useState([]);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [roles, setRoles] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [tabData, setTabData] = useState({});
  const [originalTabData, setOriginalTabData] = useState({}); // Store original data for comparison
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingTabKey, setPendingTabKey] = useState(null); // Store the tab user wants to switch to
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [permissionHeaders, setPermissionHeaders] = useState([]);
  const [showDeleteDrawer, setShowDeleteDrawer] = useState(false);

  const fetchTabList = async () => {
    setLoading(true);
    try {
      const res = await instance.get(ROUTES.RBAC_GET_HIERARCHY_OPTIONS);
      const parentRoles = [
        ...(res.data?.data?.parent_roles || []),
        ...(res.data?.data?.available_roles || [])
      ];
      setTabList(parentRoles);
      if (parentRoles.length > 0) {
        setActiveKey(parentRoles[0].id.toString());
      }
    } catch (err) {
      console.error(err);
      const errorMessage = extractErrorMessage(err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTabList();
  }, []);

  // Load tab content when tab changes
  useEffect(() => {
    const fetchTabContent = async () => {
      if (!activeKey) return;
      setLoading(true);
      try {
        const res = await instance.get(ROUTES.RBAC_GET_ROLE_DETAILS(activeKey));
        const featurePermissions = res.data?.data?.permissions_by_feature || [];

        // Get unique permission names from all features
        const uniqueNames = Array.from(
          new Set(
            featurePermissions.flatMap(feature =>
              feature.permissions.map(perm => perm.name)
            )
          )
        );

        // Update both current and original data
        setTabData(prev => ({
          ...prev,
          [activeKey]: featurePermissions
        }));

        setOriginalTabData(prev => ({
          ...prev,
          [activeKey]: JSON.parse(JSON.stringify(featurePermissions)) // Deep copy
        }));

        setPermissionHeaders(uniqueNames);
        setHasUnsavedChanges(false); // Reset unsaved changes when loading new tab
      } catch (err) {
        console.error(err);
        toast.error('Failed to load tab content', {
          position: 'top-center'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTabContent();
  }, [activeKey]);

  // Check if current tab has unsaved changes
  const checkForUnsavedChanges = (currentTabKey) => {
    if (!currentTabKey || !tabData[currentTabKey] || !originalTabData[currentTabKey]) {
      console.log(currentTabKey, tabData[currentTabKey], originalTabData[currentTabKey])
      return false;
    }
    console.log(currentTabKey, tabData[currentTabKey], originalTabData[currentTabKey])
    const current = JSON.stringify(tabData[currentTabKey]);
    const original = JSON.stringify(originalTabData[currentTabKey]);
    return current !== original;
  };

  const handleTabSelect = (key) => {
    const hasChanges = checkForUnsavedChanges(activeKey);

    if (hasChanges) {
      // Show confirmation modal
      setPendingTabKey(key);
      setShowSaveModal(true);
    } else {
      // No changes, switch directly
      setActiveKey(key);
      setHasUnsavedChanges(false);
    }
  };

  const handlePermissionToggle = (roleId, roleName, perm) => {
    // Update only local state, don't call API
    const currentTabKey = roleId.toString();
    const currentPermissions = tabData[currentTabKey] || [];

    const updatedTab = currentPermissions.map(f => ({
      ...f,
      permissions: f.permissions.map(p =>
        p.id === perm.id ? { ...p, assigned: !p.assigned } : p
      )
    }));

    setTabData(prev => ({
      ...prev,
      [currentTabKey]: updatedTab
    }));

    // Check if this creates unsaved changes
    const updatedCurrent = JSON.stringify(updatedTab);
    const original = JSON.stringify(originalTabData[currentTabKey] || []);
    setHasUnsavedChanges(updatedCurrent !== original);
  };

  const handleSaveChanges = async () => {
    if (!activeKey) return;

    setSaving(true);
    try {
      const currentPermissions = tabData[activeKey] || [];

      // Flatten all permissions for the current tab
      const allPermissions = currentPermissions.flatMap(f => f.permissions);

      // Filter only assigned permissions
      const assignedCodenames = allPermissions
        .filter(p => p.assigned)
        .map(p => p.codename);

      const roleName = tabList.find(t => t.id.toString() === activeKey)?.name || '';

      await instance.post(ROUTES.RBAC_CREATE_ROLE, {
        id: parseInt(activeKey),
        role_name: roleName,
        permission_ids: assignedCodenames
      });

      // Update original data to match current data
      setOriginalTabData(prev => ({
        ...prev,
        [activeKey]: JSON.parse(JSON.stringify(tabData[activeKey]))
      }));

      setHasUnsavedChanges(false);
      toast.success('Changes saved successfully!');

    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleModalSaveAndSwitch = async () => {
    await handleSaveChanges();
    setShowSaveModal(false);
    if (pendingTabKey) {
      setActiveKey(pendingTabKey);
      setPendingTabKey(null);
    }
  };

  const handleModalDiscardAndSwitch = () => {
    // Revert changes to original data
    if (activeKey && originalTabData[activeKey]) {
      setTabData(prev => ({
        ...prev,
        [activeKey]: JSON.parse(JSON.stringify(originalTabData[activeKey]))
      }));
    }

    setShowSaveModal(false);
    setHasUnsavedChanges(false);

    if (pendingTabKey) {
      setActiveKey(pendingTabKey);
      setPendingTabKey(null);
    }
  };

  const handleCreateRoleClick = () => {
    setShowCreateRole(true);
  };
  const handleDeleteRoleClick = () => {
    // Replace this with actual delete logic
    console.log("Delete role clicked");
  };

  const handleCloseCreateRole = () => {
    setShowCreateRole(false);
  };

  const handleRoleCreated = () => {
    fetchTabList();
  };

  return (
    <Base>
      <div className="container">
        {error && (
          <Alert
            variant="danger"
            className="mb-4"
            dismissible
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        <Row>
          <Col className="mr-auto text-center">
            <Breadcrumb>
              <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: '/' }}>Dashboard</Breadcrumb.Item>
              <Breadcrumb.Item active>Roles & Permissions</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        <div className="flex justify-between items-center ">
          <h1 className="text-[28px] font-bold text-gray-800">Roles & Permissions</h1>
          {/* Buttons container */}
          <div className="flex items-center gap-2">
            {/* Delete Button */}
            <button
              className="text-danger px-3 py-2 border border-red-600 rounded-md shadow-sm text-sm font-semibold text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
              onClick={() => setShowDeleteDrawer(true)}
              variant="link"
              id="deleteRoleBtn"
            >
              <span className="material-icons pe-2 text-base">delete</span>
              Delete Roles
            </button>

            <button
              className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
              id="createRoleBtn"
              onClick={handleCreateRoleClick}
              disabled={loading}
            >
              <FaPlus className="me-1" ></FaPlus>
              Create New Role
            </button>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow mt-2 ">
          <div className="border-b border-gray-200 overflow-x-auto overflow-y-hidden ">
            <nav className="-mb-px flex space-x-6  px-6 pt-4 overflow-x-auto">
              {tabList.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabSelect(tab.id.toString())}
                  className={`whitespace-nowrap pb-2 px-3 border-b-2 font-medium text-md ${activeKey === tab.id.toString()
                    ? 'tab-active border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-transparent'
                    }`}
                >
                  {truncateText(tab.name, 20)}
                  {/* Show indicator for unsaved changes */}
                  {activeKey === tab.id.toString() && hasUnsavedChanges && (
                    <span className="ml-1 text-red-500">*</span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Spinner animation="border" />
              </div>
            ) : (
              <table className="min-w-full no-vertical-border divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr className='border-0'>
                    <th className="sticky top-0 left-0 z-40 bg-gray-50 border-r border-gray-200 px-3 py-3 text-left text-md font-semibold tracking-wider whitespace-nowrap">
                      Feature
                    </th>
                    {permissionHeaders.map((name) => (
                      <th
                        key={name}
                        className="sticky top-0 z-30 bg-gray-50 px-3 py-3 text-center text-md font-semibold tracking-wider whitespace-nowrap"
                      >
                        {name
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white ">
                  {(tabData[activeKey] || []).map((feature) => (
                    <tr key={feature.feature_id} className='m-0 p-0 border-0' >
                      <td className="sticky left-0 z-20 bg-white px-4 py-3 text-left whitespace-nowrap text-md text-gray-900 border-r border-gray-100 ">
                        {feature.feature_name}
                      </td>
                      {permissionHeaders.map((name) => {
                        const perm = feature.permissions.find((p) => p.name === name);
                        return (
                          <td key={name} className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 text-center">
                            {perm ? (
                              <input
                                type="checkbox"
                                className="checkbox-custom"
                                checked={perm.assigned}
                                onChange={() =>
                                  handlePermissionToggle(
                                    parseInt(activeKey),
                                    tabList.find(t => t.id.toString() === activeKey)?.name || '',
                                    perm
                                  )
                                }
                              />
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <Button
            variant="secondary no-hover"
            className='border fw-bold'
            onClick={handleModalDiscardAndSwitch}
          >
            Cancel
          </Button>
          <button
            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-bold text-white ${hasUnsavedChanges
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            type="button"
            onClick={handleSaveChanges}
            disabled={!hasUnsavedChanges || saving}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>

        {/* Save Confirmation Modal */}
        <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)} centered className='border-0'>
          <Modal.Header closeButton>
            <Modal.Title>Unsaved Changes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Save your changes before moving to the next role.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleModalDiscardAndSwitch} className='fw-bold'>
              Discard
            </Button>
            <Button variant="primary" onClick={handleModalSaveAndSwitch} disabled={saving} className='fw-bold'>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <CreateRoleOffCanvas
          show={showCreateRole}
          onHide={handleCloseCreateRole}
          onRoleCreated={handleRoleCreated}
        />
        <DeleteRoles
          show={showDeleteDrawer}
          onHide={() => setShowDeleteDrawer(false)}
          onRoleDeleted={() => {
            fetchTabList()
          }}
        />

      </div>
    </Base>
  );
};

export default ViewRoles;